import type {
  CatalogItemImplementationFilterExpression,
  IndexPaging,
  Query,
  WatchedCatalogItemFilter,
} from '../inputs';
import { requestExperience } from '../network';
import { WatchedCatalogItem } from '../objects';

export type CatalogWatchedListArgs = {
  catalogItem?: CatalogItemImplementationFilterExpression;
  filter?: WatchedCatalogItemFilter;
  page?: IndexPaging;
};

/**
 * @see https://latest.api-docs-develop.ott.kaltura.com/api-docs/Experience_API/Query
 */
export async function fetchWatchedList(variables: CatalogWatchedListArgs) {
  const res = await requestExperience<Query>(
    `
      query Query($filter: WatchedCatalogItemFilter, $catalogItem: CatalogItemImplementationFilterExpression!, $page: IndexPaging, $config: ContextConfiguration) {
        catalog(contextConfiguration: $config) {
          watchedList(filter: $filter, catalogItem: $catalogItem, page: $page) {
            totalCount
            items {
              position
              duration
              hasFinishedWatching
              catalogItem {
                id
                name
                type
                images {
                  imageType {
                    id
                    ratio {
                      name
                    }
                  }
                  url
                }
                ... on Program {
                  endDate
                  linearChannelId
                  startDate
                  metadata(
                    keys: [
                      "ParentalRating",
                      "MosaicInfo",
                      "IsMosaicEvent"
                    ]
                  )
                }
                ... on ProgramEpisode {
                  endDate
                  linearChannelId
                  startDate
                  metadata(
                    keys: [
                      "EpisodeNumber",
                      "EpisodeName",
                      "SeasonNumber",
                      "SeriesID",
                      "ParentalRating",
                      "MosaicInfo",
                      "IsMosaicEvent"
                    ]
                  )
                }
                ... on VodMovie {
                  metadata(
                    keys: [
                      "ParentalRating",
                      "MarkerBackgroundColor",
                      "ContentMarkerText",
                      "MarkerTextColor"
                    ]
                  )
                }
                ... on VodEpisode {
                  episodeNumber
                  season {
                    seasonNumber
                  }
                  series {
                    seriesId
                  }
                  metadata(
                    keys: [
                      "ParentalRating",
                      "MarkerBackgroundColor",
                      "ContentMarkerText",
                      "MarkerTextColor"
                    ]
                  )
                }
                ... on RecordingCatalogItem {
                  endDate
                  linearChannelId
                  startDate
                  metadata(
                    keys: [
                      "ParentalRating",
                      "MosaicInfo",
                      "IsMosaicEvent"
                    ]
                  )
                  recording {
                    id
                  }
                }
                ... on RecordingEpisodeCatalogItem {
                  endDate
                  linearChannelId
                  startDate
                  metadata(
                    keys: [
                      "EpisodeNumber",
                      "EpisodeName",
                      "SeasonNumber",
                      "SeriesID",
                      "ParentalRating",
                      "MosaicInfo",
                      "IsMosaicEvent"
                    ]
                  )
                  recording {
                    id
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables,
  );

  const { items, totalCount } = res.data.catalog?.watchedList ?? {
    items: [] as WatchedCatalogItem[],
    totalCount: 0,
  };

  return { items, totalCount };
}
